import { useState } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { useOpsStore } from "../state/opsStore";
import { useOpsSnapshot } from "../hooks/useOpsSnapshot";
import { useOpsLiveUpdates } from "../hooks/useOpsLiveUpdates";
import { useWsClient } from "@/lib/ws-provider";
import { useEscalationAlerts } from "@/features/queues/hooks/useEscalationAlerts";
import { SlaWatchlist } from "../components/SlaWatchlist";
import { OperatorGrid } from "../components/OperatorGrid";
import { BottleneckHeatmap } from "../components/BottleneckHeatmap";
import { QueueDepthSparklines } from "../components/QueueDepthSparklines";
import { OperatorDetailDrawer } from "../components/OperatorDetailDrawer";
import { EvictionList } from "@/features/queue-evictions/components/EvictionList";
import "./OpsConsolePage.css";

/**
 * Phase 18 WU-18.D8 — Ops Console page.
 *
 * Four live panels (SLA Watchlist, Operator Grid, Bottleneck Heatmap,
 * Queue Depth) + operator drawer. Permission-gated on
 * `supervisor.ops_console` — the API enforces it too, this is a client-
 * side guard to avoid a blank page + 403 flash.
 *
 * On mount:
 *   1. useOpsSnapshot()       → seeds opsStore from 3 /api/ops/* endpoints
 *   2. useOpsLiveUpdates()    → opens WS + routes live events to the store
 *   3. reconnecting banner    → shown when WS stale > 30s
 *
 * Batch row click → /analytics/batches/:id/trail (drill-through from D7).
 * Operator row click → right-side drawer (D7).
 */
export function OpsConsolePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const reconnecting = useOpsStore((s) => s.reconnecting);
  const tenantId = user?.tenantId ?? null;
  const [drawerOperatorId, setDrawerOperatorId] = useState<string | null>(null);

  const hasPermission = user?.permissions?.includes("supervisor.ops_console") ?? false;

  useOpsSnapshot();
  useOpsLiveUpdates(hasPermission ? tenantId : null);
  // WU-1.5 — supervisors watching the ops console also get the live
  // "Document escalated" toast (permission-gated inside the hook).
  useEscalationAlerts(useWsClient());

  if (user && !hasPermission) {
    return (
      <div className="ops-console">
        <header className="ops-console__header">
          <h1 className="ops-console__title">Ops Console</h1>
        </header>
        <div className="ops-console__forbidden" data-testid="ops-console-forbidden">
          You need the <strong>supervisor.ops_console</strong> permission to view
          this page.
        </div>
      </div>
    );
  }

  return (
    <div className="ops-console">
      <header className="ops-console__header">
        <h1 className="ops-console__title">Ops Console</h1>
        {reconnecting && (
          <div
            className="ops-console__banner--warn"
            data-testid="ops-console-reconnecting"
          >
            Reconnecting…
          </div>
        )}
      </header>
      <section className="ops-console__grid">
        <div className="ops-console__pane" data-testid="ops-pane-sla">
          <h2>SLA Watchlist</h2>
          <SlaWatchlist
            onBatchClick={(id) => navigate(`/analytics/batches/${id}/trail`)}
          />
        </div>
        <div className="ops-console__pane" data-testid="ops-pane-operators">
          <h2>Operators</h2>
          <OperatorGrid onOperatorClick={setDrawerOperatorId} />
        </div>
        <div className="ops-console__pane" data-testid="ops-pane-bottlenecks">
          <h2>Bottlenecks (last 15m)</h2>
          <BottleneckHeatmap />
        </div>
        <div className="ops-console__pane" data-testid="ops-pane-queue-depth">
          <h2>Queue Depth</h2>
          <QueueDepthSparklines />
        </div>
      </section>
      {/* WU-2.4 T10 — self-contained; renders nothing for viewers without
          supervisor.queue_evict. */}
      <EvictionList />
      <OperatorDetailDrawer
        operatorId={drawerOperatorId}
        onClose={() => setDrawerOperatorId(null)}
      />
    </div>
  );
}
